import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import type { PlaceMapItem, UnnamedPin } from '../components/places/PlacesMap'

interface PlacesMapResponse {
    places: PlaceMapItem[]
    unnamed_pins: UnnamedPin[]
    checkin_dates?: string[]
}

/**
 * Shared fetch for `/api/places/map`. Used by PlacesCollectionPage (full
 * surface) and CollectionsPage (preview map at the top of the list). The
 * endpoint is user-wide when called without arguments, which is exactly what
 * both callers want here.
 */
export const usePlacesMap = () => {
    const [places, setPlaces] = useState<PlaceMapItem[]>([])
    const [unnamedPins, setUnnamedPins] = useState<UnnamedPin[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const reload = useCallback(async () => {
        try {
            setLoading(true)
            const resp = await api.get<PlacesMapResponse>('/api/places/map')
            setPlaces(resp.data.places)
            setUnnamedPins(resp.data.unnamed_pins)
            setError(null)
        } catch (err) {
            console.error('Failed to load places map:', err)
            setError('Failed to load places.')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        reload()
    }, [reload])

    return { places, unnamedPins, loading, error, reload }
}
